import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export interface TemplateResponse {
  id: string;
  name: string;
  content: string;
  category: string;
  lastUsed?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface TemplateInput {
  name: string;
  content: string;
  category: string;
  customCategory?: string;
}

export interface TemplateRecord extends TemplateResponse {
  characters: number;
  smsCount: number;
  lastUsed: string;
}

const SMS_SEGMENT_LENGTH = 160;

const mapTemplate = (template: TemplateResponse): TemplateRecord => {
  const characters = template.content?.length ?? 0;
  const smsCount = characters === 0 ? 0 : Math.ceil(characters / SMS_SEGMENT_LENGTH);
  const lastUsed = template.lastUsed || template.updatedAt || template.createdAt || '';

  return {
    ...template,
    characters,
    smsCount,
    lastUsed
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Templates API request failed');
  }

  const json = await response.json();
  return (json.data ?? json) as T;
};

export const fetchUserTemplates = async (): Promise<TemplateRecord[]> => {
  const res = await fetch(`${API_BASE}/templates`, createAuthRequestInit());

  const data = await handleResponse<{ templates?: TemplateResponse[]; items?: TemplateResponse[]; }>(res);
  const list = data.templates ?? data.items ?? ([] as TemplateResponse[]);
  return list.map(mapTemplate);
};

export const fetchTemplateLibrary = async (): Promise<TemplateRecord[]> => {
  const res = await fetch(`${API_BASE}/templates/library`, createAuthRequestInit());

  const data = await handleResponse<{ templates?: TemplateResponse[]; items?: TemplateResponse[]; }>(res);
  const list = data.templates ?? data.items ?? ([] as TemplateResponse[]);
  return list.map(mapTemplate);
};

export const createTemplate = async (payload: TemplateInput): Promise<TemplateRecord> => {
  const res = await fetch(
    `${API_BASE}/templates`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );

  const data = await handleResponse<{ template: TemplateResponse }>(res);
  return mapTemplate(data.template);
};

export const updateTemplate = async (id: string, payload: Partial<TemplateInput>): Promise<TemplateRecord> => {
  const res = await fetch(
    `${API_BASE}/templates/${id}`,
    createAuthRequestInit(
      {
        method: 'PUT',
        body: JSON.stringify(payload)
      },
      true
    )
  );

  const data = await handleResponse<{ template: TemplateResponse }>(res);
  return mapTemplate(data.template);
};

export const deleteTemplate = async (id: string): Promise<void> => {
  const res = await fetch(
    `${API_BASE}/templates/${id}`,
    createAuthRequestInit({
      method: 'DELETE'
    })
  );

  await handleResponse(res);
};

export const importTemplateFromLibrary = async (templateId: string): Promise<TemplateRecord> => {
  const res = await fetch(
    `${API_BASE}/templates/library/${templateId}/import`,
    createAuthRequestInit({
      method: 'POST'
    })
  );

  const data = await handleResponse<{ template: TemplateResponse }>(res);
  return mapTemplate(data.template);
};


